// Mail-Eingang (Runde 6, Agent 6A): Typen und Client nach crm/API.md, Abschnitt 21.1. Vorarbeit: die Formen stehen, die Routen
// antworten bis zum Bau von 6A mit leeren Listen bzw. 501 `nicht_umgesetzt`. 6A erweitert diese Datei nur additiv.
import { http } from "./client";
import type { Liste, ListenParameter } from "./types";

export type MailZuordnung = "zugeordnet" | "mehrdeutig" | "unzugeordnet" | "intern" | "ignoriert";
export type MailRichtung = "eingehend" | "ausgehend";

export interface MailAdresseDaten {
  adresse: string;
  name: string | null;
}

export interface MailAnhang {
  /** ID in kommunikation_dateien; Download: `/api/mail/dateien/{id}` */
  datei_id: string;
  dateiname: string;
  content_type: string | null;
  groesse: number;
  inline_cid: string | null;
}

/** Zeile in Liste und Inbox */
export interface MailZeile {
  id: string;
  /** Postfach, in dem die Mail liegt */
  user_id: string;
  richtung: MailRichtung;
  von: MailAdresseDaten;
  an: MailAdresseDaten[];
  betreff: string | null;
  vorschau: string | null;
  datum: string | null;
  hat_anhang: boolean;
  zuordnung: MailZuordnung;
  lead_id: string | null;
  lead_name: string | null;
  contact_id: string | null;
  /** Aktivität `Email` im Verlauf, wenn zugeordnet */
  activity_id: string | null;
  thread_key: string | null;
  /** bei "mehrdeutig": mögliche Leads */
  kandidaten: { lead_id: string; lead_name: string | null }[];
  gelesen: boolean;
}

/** Eine Mail mit Text und Anhängen */
export interface MailDetail extends MailZeile {
  cc: MailAdresseDaten[];
  text: string | null;
  html: string | null;
  anhaenge: MailAnhang[];
}

export interface MailZaehler {
  /** ungelesene eingehende Mails des Nutzers */
  ungelesen: number;
  /** Mails, die eine Entscheidung brauchen (unzugeordnet, mehrdeutig) */
  offen: number;
}

export interface MailKontoStand {
  user_id: string;
  /** Name des Nutzers (additiv, 6A) */
  name?: string;
  adresse: string;
  /** Eingang eingeschaltet (`email_accounts.eingang_aktiv`) */
  aktiv: boolean;
  eingang_ab: string | null;
  status: "aus" | "ok" | "laeuft" | "fehler";
  letzter_lauf_at: string | null;
  letzter_erfolg_at: string | null;
  letzter_fehler: string | null;
  anzahl_gesamt: number;
  anzahl_neu: number;
  /** älteste schon geholte Mail */
  nachgeholt_bis: string | null;
}

export interface MailEingangEinstellungen {
  interne_domains: string[];
  interne_adressen: string[];
  /** zusätzlich als intern behandelt: Domains und Adressen der eigenen Postfächer und Nutzer (nur Anzeige, additiv, 6A) */
  automatisch_domains?: string[];
  automatisch_adressen?: string[];
  /** neue Mails auch in der Glocke melden (Standard aus, additiv, 6A) */
  glocke?: boolean;
  /** Mails dieser Absender ohne Lead werden gleich weggelegt (additiv, 6A) */
  ignorierte_absender?: string[];
}

/** Ergebnis eines Abgleichs (additiv, 6A): neu abgelegte und übersprungene Mails, `fehler` bei Problemen mit dem Postfach */
export interface MailAbgleichErgebnis {
  neu: number;
  uebersprungen: number;
  fehler: string | null;
}

export interface MailKontoAntwort {
  konto: MailKontoStand;
  ergebnis?: MailAbgleichErgebnis;
}

export interface MailEingangParameter extends ListenParameter {
  zuordnung?: MailZuordnung[];
  lead_id?: string;
  nur_ungelesen?: boolean;
  /** Postfach; leer = alle sichtbaren */
  user_id?: string;
  q?: string;
}

export const mailEingangApi = {
  status: () => http.get<{ konten: MailKontoStand[] }>("/api/mail-eingang/status"),
  zaehler: () => http.get<MailZaehler>("/api/mail-eingang/zaehler"),
  einstellungen: () => http.get<MailEingangEinstellungen>("/api/mail-eingang/einstellungen"),
  einstellungenSetzen: (e: Partial<Pick<MailEingangEinstellungen, "interne_domains" | "interne_adressen" | "glocke" | "ignorierte_absender">>) =>
    http.put<MailEingangEinstellungen>("/api/mail-eingang/einstellungen", e),
  abgleichen: (userId?: string) => http.post<MailKontoAntwort>("/api/mail-eingang/abgleich", userId ? { user_id: userId } : {}),
  nachholen: (ab: string, userId?: string) => http.post<MailKontoAntwort>("/api/mail-eingang/nachholen", { ab, user_id: userId }),
  kontoSchalten: (userId: string, aktiv: boolean, ab?: string) =>
    http.put<MailKontoAntwort>(`/api/mail-eingang/konten/${encodeURIComponent(userId)}`, { aktiv, ab }),
  liste: (p: MailEingangParameter = {}, signal?: AbortSignal) =>
    http.get<Liste<MailZeile>>(
      "/api/mail-eingang",
      { limit: p.limit, cursor: p.cursor, zuordnung: p.zuordnung, lead_id: p.lead_id, nur_ungelesen: p.nur_ungelesen, user_id: p.user_id, q: p.q },
      signal,
    ),
  eine: (id: string) => http.get<{ nachricht: MailDetail }>(`/api/mail-eingang/${encodeURIComponent(id)}`),
  gelesen: (id: string, gelesen = true) => http.post<{ nachricht: MailZeile }>(`/api/mail-eingang/${encodeURIComponent(id)}/gelesen`, { gelesen }),
  /** `auchWeitere`: offene Mails desselben Absenders im selben Postfach gleich mit zuordnen (Antwort: `weitere`) */
  zuordnen: (id: string, leadId: string, contactId?: string, auchWeitere = false) =>
    http.post<{ nachricht: MailZeile; weitere?: number }>(`/api/mail-eingang/${encodeURIComponent(id)}/zuordnen`, {
      lead_id: leadId,
      contact_id: contactId,
      auch_weitere: auchWeitere,
    }),
  /** `absenderImmer`: auch offene und künftige Mails dieses Absenders weglegen (Antwort: `weitere`) */
  ignorieren: (id: string, absenderImmer = false) =>
    http.post<{ nachricht: MailZeile; weitere?: number }>(`/api/mail-eingang/${encodeURIComponent(id)}/ignorieren`, absenderImmer ? { absender_immer: true } : {}),
};
